import type { TJSONSchema } from "@artifact-management/json-dynamic-form";
import { UrlInput } from "@inputs/url";
import { useRef, useState } from "react";
import { type FieldValues, type Path, useController } from "react-hook-form";
import type { IFormGenInputProps } from "./index";

export const FormGenInputUrl = <Schema extends TJSONSchema, T extends FieldValues>({
  control,
  setValue,
  uiSchemaElement,
  context,
}: IFormGenInputProps<Schema, T>) => {
  const lookup = context?.url?.[uiSchemaElement.scope];
  const { field } = useController({ control, name: uiSchemaElement.scope as unknown as Path<T> });
  const [loading, setLoading] = useState(false);
  const [searchDone, setSearchDone] = useState(false);
  const [found, setFound] = useState(false);
  const latestSearchedValue = useRef<string>();

  const handleSearch = async (value: string) => {
    if (!lookup) {
      return;
    }
    latestSearchedValue.current = value;
    setLoading(true);
    try {
      const overrides = await lookup.fetch(value);
      if (latestSearchedValue.current !== value) {
        return;
      }
      setFound(Boolean(overrides));
      if (overrides) {
        for (const [key, overrideValue] of Object.entries(overrides)) {
          setValue(key as Path<T>, overrideValue as never);
        }
      }
    } catch {
    } finally {
      if (latestSearchedValue.current === value) {
        setLoading(false);
        setSearchDone(true);
      }
    }
  };

  const cancelSearch = () => {
    lookup?.cancel?.();
    setLoading(false);
  };

  return (
    <UrlInput
      fullWidth
      url={(field.value as string | undefined) ?? ""}
      onUrlChange={(value) => {
        field.onChange(value);
        setSearchDone(false);
        setFound(false);
        for (const [key, resetValue] of Object.entries(lookup?.resetOverrides ?? {})) {
          setValue(key as Path<T>, resetValue as never);
        }
      }}
      onSearch={handleSearch}
      onCancelSearch={cancelSearch}
      loading={loading}
      searchDone={searchDone}
      found={found}
    />
  );
};
